import React from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { siteConfig } from "../siteConfig";
import ServiceCard from "../components/ServiceCard";
import QuoteFormEmbed from "../components/QuoteFormEmbed";
import Reveal from "../components/Reveal";

const toTitleCase = (slug) =>
  slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const ServiceAreaDetailPage = () => {
  const { areaSlug } = useParams();
  const areaName = toTitleCase(areaSlug);
  const isValidArea = siteConfig.serviceAreas.some(
    (a) => a.toLowerCase().replace(/\s+/g, "-") === areaSlug
  );

  if (!isValidArea) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="font-display font-black text-3xl mb-4" style={{ color: siteConfig.colors.dark }}>Area not found</h1>
          <Link to="/service-areas" style={{ color: siteConfig.colors.primary }} className="font-semibold">← Back to Service Areas</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section
        className="relative flex items-end"
        style={{
          minHeight: "38vh",
          backgroundImage: `url(${siteConfig.hero.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 100%)" }} />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 pb-12 pt-32">
          <p className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: siteConfig.colors.primary }}>
            {siteConfig.businessName}
          </p>
          <h1 className="font-display font-black text-white leading-tight mb-3" style={{ fontSize: "clamp(2.2rem,5vw,3.75rem)" }}>
            Serving {areaName}, {siteConfig.state}
          </h1>
          <p className="text-white/70 text-lg max-w-2xl">
            {siteConfig.serviceAreaPage.heroSub}
          </p>
        </div>
      </section>

      {/* Content + Form */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-3 gap-10">
          <Reveal as="div" className="lg:col-span-2">
            <h2 className="font-display font-black text-2xl mb-4" style={{ color: siteConfig.colors.dark }}>
              {siteConfig.businessName} in {areaName}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              {siteConfig.businessName} {siteConfig.serviceAreaPage.bodyIntro}
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              {siteConfig.serviceAreaPage.bodySecondary}
            </p>

            <h3 className="font-display font-black text-xl mb-4" style={{ color: siteConfig.colors.dark }}>
              Services available in {areaName}
            </h3>
            <ul className="space-y-2 mb-8">
              {siteConfig.services.map((s) => (
                <li key={s.slug} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 shrink-0" style={{ color: siteConfig.colors.primary }} />
                  <Link to={`/services/${s.slug}`} className="text-gray-600 hover:underline">{s.name}</Link>
                </li>
              ))}
            </ul>

            <Link
              to="/service-areas"
              className="text-sm font-semibold"
              style={{ color: siteConfig.colors.primary }}
            >
              ← View all service areas
            </Link>
          </Reveal>

          <Reveal as="div" animation="right" className="lg:col-span-1">
            <div className="sticky top-24">
              <QuoteFormEmbed title={`Free Estimate in ${areaName}`} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map — centered on this service area */}
      <section className="pb-20 bg-white">
        <div className="max-w-2xl mx-auto px-5 md:px-8">
          <Reveal as="div" className="rounded-2xl overflow-hidden border border-gray-200 shadow-md w-full" style={{ height: "420px" }}>
            <iframe
              src={`https://maps.google.com/maps?q=${encodeURIComponent(`${areaName}, ${siteConfig.state}`)}&output=embed`}
              title={`Map of ${areaName}, ${siteConfig.state}`}
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-16" style={{ backgroundColor: siteConfig.colors.light }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <h2 className="font-display font-black text-2xl mb-8" style={{ color: siteConfig.colors.dark }}>Our Services</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {siteConfig.services.slice(0, 3).map((s, i) => (
              <Reveal key={s.slug} delay={i * 100}>
                <ServiceCard service={s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

    </>
  );
};

export default ServiceAreaDetailPage;
